// UsersAdminTab.jsx — Admin Panel "Users" sub-tab, extracted from
// LimitedPartnerPortal. Member list with inline role/chapter editing, profile
// photo upload, sign-in email change, magic-link resend, and delete. The
// editingUsers / uploadingPhotoFor / updatingEmailFor maps stay lifted in the
// portal so drafts survive sub-tab switches (this component unmounts when the
// admin switches sub-tabs); uploadingPhotoFor is also shared with the Grant
// Winners and Chapter sub-tabs.
import React from "react";
import { doc, updateDoc, deleteField, Timestamp } from "firebase/firestore";
import { signOut } from "firebase/auth";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { httpsCallable } from "firebase/functions";
import { db, auth, storage, functions } from "../../../../firebaseConfig.js";
import { RetroButton, EmptyState } from "../../../ui/retro";
import { dashIfEmpty } from "../../../../helpers/labels";
import { VALID_ROLES } from "../../../../data/roles";

const updateUserEmailCallable = httpsCallable(functions, "updateUserEmail");
const deleteUserCallable = httpsCallable(functions, "deleteUser");

// Five-column grid shared by the list header and every member row.
const USERS_GRID = "1.8fr 1.2fr 1fr 1.4fr 96px";

/**
 * Generic person-silhouette avatar for members with no photo. Rendered
 * underneath every member <img> so a broken legacy /assets/lps/ URL falls
 * back to it (the onError handlers hide the img, revealing this), and used
 * directly when there's no photo URL at all. Same ink border as
 * .member-photo so list rows and edit panels keep a consistent shape.
 */
function MemberAvatarPlaceholder({ size = 32, className = '', style }) {
  return (
    <div
      className={`member-photo ${className}`}
      aria-hidden="true"
      style={{
        width: size, height: size, flexShrink: 0, boxSizing: 'border-box',
        background: 'var(--mb-paper-deep)', overflow: 'hidden',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        ...style,
      }}
    >
      <svg viewBox="0 0 24 24" width="82%" height="82%" fill="var(--mb-ink-60)" style={{ display: 'block' }}>
        <path d="M12 11.4c2.32 0 4.2-1.9 4.2-4.25S14.32 2.9 12 2.9 7.8 4.8 7.8 7.15 9.68 11.4 12 11.4zm0 2.1c-3.5 0-8.4 1.75-8.4 5.25V24h16.8v-5.25c0-3.5-4.9-5.25-8.4-5.25z" />
      </svg>
    </div>
  );
}

export default function UsersAdminTab({
  user,
  users,
  sortedUsers,
  chapters,
  activeChapterNames,
  isAdmin,
  isSuperAdmin,
  isChapterDirector,
  isLP,
  userChapter,
  editingUsers,
  setEditingUsers,
  uploadingPhotoFor,
  setUploadingPhotoFor,
  updatingEmailFor,
  setUpdatingEmailFor,
  loadAdminData,
  loadChapterMembers,
  requestConfirm,
  showAppAlert,
  showAppConfirm,
  sendSignInLinkCallable,
}) {
const handleDeleteUser = async (userIdToDelete, userEmailToDelete) => {
  if (!userIdToDelete) {
    showAppAlert("Delete failed: Missing user ID.");
    return;
  }
  if (userIdToDelete === user.uid) {
    showAppAlert("You cannot delete your own user account.");
    return;
  }

  const userToDeleteData = users.find(u => u.id === userIdToDelete);
  if (userToDeleteData?.role === 'superAdmin' && !isSuperAdmin) {
    showAppAlert("Admins cannot delete SuperAdmin accounts.");
    return;
  }
  if (isChapterDirector && userToDeleteData?.chapter && userToDeleteData.chapter !== userChapter) {
    showAppAlert(`Chapter directors can only delete users in ${userChapter}.`);
    return;
  }

  // Retro-styled confirm. The `deleteUser` callable removes the Firebase Auth
  // account AND the /users doc together, so this is a full, permanent removal.
  const ok = await requestConfirm({
    title: `Delete ${userEmailToDelete || 'this user'}?`,
    confirmLabel: 'Delete member',
    cancelLabel: 'Keep',
    variant: 'danger',
    message: (
      <>
        <p>This permanently deletes their sign-in account and their profile data (role, chapter, name, bio, badges).</p>
        <p><strong>This cannot be undone.</strong> Any open session of theirs expires within about an hour. To bring them back later, re-invite them.</p>
      </>
    ),
  });
  if (ok) {
    try {
      const result = await deleteUserCallable({ uid: userIdToDelete });
      const data = result?.data || {};
      showAppAlert(
        data.authDeleted
          ? `${userEmailToDelete || userIdToDelete} deleted — sign-in account and profile are both gone.`
          : `Profile for ${userEmailToDelete || userIdToDelete} deleted. They had no sign-in account, so there was nothing else to remove.`
      );
      loadAdminData(); // Refresh user list
    } catch (error) {
      console.error(`LPPortal: Error deleting user ${userIdToDelete}:`, error);
      showAppAlert(`Delete failed: ${error?.message || 'Unknown error.'}`);
    }
  }
};

const handleUpdateUser = async (userIdToUpdate, field, value) => {
  // --- Validation ---
  if (!userIdToUpdate || !field) {
    console.error("LPPortal: Update user failed - missing userId or field");
    return; // Should not happen
  }
  const targetUser = users.find(u => u.id === userIdToUpdate);
  if (!targetUser) {
    console.error(`LPPortal: Update user failed - user ${userIdToUpdate} not found in local state.`);
    showAppAlert("Update failed: User not found.");
    loadAdminData(); // Refresh to be safe
    return;
  }

  // Prevent non-SuperAdmins from changing their own role/chapter or elevating others to SuperAdmin
  if (!isSuperAdmin) {
    if (userIdToUpdate === user.uid && (field === 'role' || field === 'chapter')) {
      showAppAlert("You cannot change your own role or chapter. Ask a SuperAdmin.");
      loadAdminData(); // Revert optimistic UI if needed
      return;
    }
    if (field === 'role' && value === 'superAdmin') {
      showAppAlert("Only SuperAdmins can assign the SuperAdmin role.");
      loadAdminData();
      return;
    }
    // Prevent Admins from changing SuperAdmins at all?
    if (targetUser.role === 'superAdmin') {
      showAppAlert("Admins cannot modify SuperAdmin accounts.");
      loadAdminData();
      return;
    }
    // LPs are read-only on other users' records. They can edit their own
    // profile (name/links/bio/photo via the self-edit rule branch) but not
    // anyone else's. Mirrors the firestore /users update policy so the user
    // gets a clear message instead of a generic permission-denied.
    if (isLP && userIdToUpdate !== user.uid) {
      showAppAlert("LPs can only edit their own profile. Ask a chapter director or SuperAdmin to update other members.");
      loadAdminData();
      return;
    }
    // Chapter directors are scoped to their own chapter.
    if (isChapterDirector) {
      if (targetUser.chapter && targetUser.chapter !== userChapter) {
        showAppAlert(`Chapter directors can only modify users in ${userChapter}.`);
        loadAdminData();
        return;
      }
      if (field === 'chapter' && value && value !== userChapter) {
        showAppAlert(`Chapter directors can only assign users to ${userChapter}.`);
        loadAdminData();
        return;
      }
    }
  }

  // Prevent assigning an invalid role
  if (field === 'role' && !VALID_ROLES.concat('unauthorized').includes(value)) { // Allow 'unauthorized' for disabling
    showAppAlert(`Invalid role selected: ${value}`);
    loadAdminData();
    return;
  }

  // chapterRole is only meaningful on superAdmin users and must be one of
  // the two presentation tiers (or empty to clear). Only superAdmins may
  // write it. Mirrors the firestore.rules chapterRoleInvariant — caught
  // client-side so the user gets a clear message.
  if (field === 'chapterRole') {
    if (!isSuperAdmin) {
      showAppAlert("Only Super Admins can set a chapter listing role.");
      loadAdminData();
      return;
    }
    if (targetUser.role !== 'superAdmin') {
      showAppAlert("Chapter listing role only applies to Super Admin accounts.");
      loadAdminData();
      return;
    }
    if (value && !['lp', 'chapter_director'].includes(value)) {
      showAppAlert(`Invalid chapter listing role: ${value}`);
      loadAdminData();
      return;
    }
  }

  try {
    const updates = { [field]: value };
    // When demoting a superAdmin, strip any stale chapterRole so the
    // firestore chapterRoleInvariant doesn't reject the write. chapterRole
    // is presentation-only and meaningless on non-superAdmin accounts.
    if (field === 'role' && value !== 'superAdmin' && targetUser.chapterRole) {
      updates.chapterRole = deleteField();
    }
    // Clearing chapterRole via the "— none —" option should remove the
    // field entirely rather than leave a null behind.
    if (field === 'chapterRole' && !value) {
      updates.chapterRole = deleteField();
    }
    await updateDoc(doc(db, "users", userIdToUpdate), updates);
    showAppAlert(`User ${targetUser.email || userIdToUpdate} updated successfully.`);
    loadAdminData(); // Refresh list with updated data
  } catch (error) {
    console.error(`LPPortal: Error updating user ${userIdToUpdate}:`, error);
    if (error.code === 'permission-denied' || error.message.includes('permission-denied')) {
      showAppAlert(`Update failed: Permission Denied. Check Firestore rules for updating user roles/chapters.`);
      console.error(`LPPortal: PERMISSION DENIED updating /users/${userIdToUpdate} by admin ${user.email}`);
    } else {
      showAppAlert(`Update failed: ${error.message}`);
    }
    loadAdminData(); // Refresh list to revert optimistic UI changes if any
  }
};

// Upload a chapter member's profile photo to Firebase Storage and propagate
// the URL to the two surfaces that render it:
//   1. /users/{uid}.photoUrl            — drives the in-portal members directory
//   2. /chapters/{slug}.lpPhotos[uid]   — drives the public static chapter pages
//      (read by public/assets/js/chapter-hydration.js, which swaps the
//      hardcoded /assets/lps/*.png src on matching <img> tags).
// Storage path is namespaced by target uid so storage.rules can resolve the
// owning chapter and enforce that only that chapter's director (or a
// superAdmin) can write.
const handleMemberPhotoUpload = async (targetUser, file) => {
  if (!targetUser?.uid || !file) return;
  // Mirrors storage.rules: superAdmin can upload for any member, chapter
  // directors only for users in their chapter, LPs only for themselves.
  if (!isSuperAdmin) {
    if (isLP && targetUser.uid !== user.uid) {
      showAppAlert("LPs can only upload their own profile photo.");
      return;
    }
    if (isChapterDirector && targetUser.chapter && targetUser.chapter !== userChapter) {
      showAppAlert(`Chapter directors can only upload photos for users in ${userChapter}.`);
      return;
    }
    if (!isLP && !isChapterDirector) {
      showAppAlert("You don't have permission to upload member photos.");
      return;
    }
  }
  if (!file.type.startsWith('image/')) {
    showAppAlert("Please choose an image file (PNG, JPG, etc.).");
    return;
  }
  if (file.size > 5 * 1024 * 1024) {
    showAppAlert("Image must be smaller than 5 MB.");
    return;
  }

  const ext = (file.name.split('.').pop() || 'jpg').toLowerCase().replace(/[^a-z0-9]/g, '') || 'jpg';
  const path = `LP-photos/${targetUser.uid}/${Date.now()}.${ext}`;

  setUploadingPhotoFor(prev => ({ ...prev, [targetUser.uid]: true }));
  try {
    const storageRef = ref(storage, path);
    await uploadBytes(storageRef, file, { contentType: file.type });
    const photoUrl = await getDownloadURL(storageRef);

    await updateDoc(doc(db, "users", targetUser.uid), { photoUrl });

    // Best-effort mirror onto the chapter doc. If it fails (e.g. chapter doc
    // not yet seeded) the in-portal directory still works off the user doc.
    const chapterDoc = chapters.find(c => c.name === targetUser.chapter);
    if (chapterDoc?.id) {
      try {
        await updateDoc(doc(db, "chapters", chapterDoc.id), {
          [`lpPhotos.${targetUser.uid}`]: {
            name: targetUser.name || '',
            photoUrl,
          },
          updatedAt: Timestamp.now(),
        });
      } catch (chapterErr) {
        console.warn(`LPPortal: photo saved on user ${targetUser.uid} but chapter doc mirror failed:`, chapterErr);
      }
    }

    showAppAlert("Member photo uploaded.");
    loadAdminData();
    loadChapterMembers();
  } catch (err) {
    console.error("LPPortal: member photo upload failed:", err);
    if (err.code === 'storage/unauthorized') {
      showAppAlert("Upload failed: you don't have permission to upload this photo.");
    } else {
      showAppAlert(`Upload failed: ${err.message || err}`);
    }
  } finally {
    setUploadingPhotoFor(prev => {
      const next = { ...prev };
      delete next[targetUser.uid];
      return next;
    });
  }
};

// Admin-initiated email change. Server-side (updateUserEmail callable) is the
// real gate — it updates Firebase Auth + the /users doc in lockstep (rolling
// back Auth if the profile write fails), revokes the member's refresh tokens,
// and notifies both the old and new address. Changing your OWN email signs
// you out here immediately — a live session under a stale identity is worse
// than a re-login.
const handleAdminChangeEmail = async (u, rawNewEmail) => {
  const newEmail = String(rawNewEmail || '').trim().toLowerCase();
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)) {
    showAppAlert("Enter a valid email address.");
    return;
  }
  if (newEmail === (u.email || '').toLowerCase()) {
    showAppAlert("That's already this member's email address.");
    return;
  }
  const isSelf = u.uid === user.uid;
  const confirmMsg = isSelf
    ? `Change your own sign-in email from ${u.email} to ${newEmail}?\n\nYou'll be signed out immediately and must sign back in with a link sent to the new address. Double-check the spelling — a typo locks you out until another admin fixes it.`
    : `Change the sign-in email for ${u.name || u.email} from ${u.email} to ${newEmail}?\n\nThey'll be notified at both addresses and sent a fresh sign-in link at the new one. Any open session of theirs expires within about an hour.`;
  if (!showAppConfirm(confirmMsg)) return;

  setUpdatingEmailFor(prev => ({ ...prev, [u.uid]: true }));
  try {
    const result = await updateUserEmailCallable({ uid: u.uid, newEmail });
    const { notifiedNew, sessionsRevoked } = result.data || {};
    let message = notifiedNew
      ? `Email updated. A sign-in link is on its way to ${newEmail}.`
      : `Email updated to ${newEmail}, but the notification email failed to send — ${isSelf
          ? 'request a sign-in link from the login form with your new address'
          : 'use "Send sign-in link" so they can get back in'}.`;
    if (sessionsRevoked === false) {
      message += " Heads up: revoking existing sessions failed; they'll expire on their own within an hour.";
    }
    showAppAlert(message);
    if (isSelf) {
      await signOut(auth);
      return;
    }
    loadAdminData();
  } catch (error) {
    console.error(`LPPortal: Email change failed for ${u.uid}:`, error);
    showAppAlert(`Email change failed: ${error.message}`);
  } finally {
    setUpdatingEmailFor(prev => {
      const next = { ...prev };
      delete next[u.uid];
      return next;
    });
  }
};

// Admin "resend sign-in link" — replaces the legacy password-reset path now
// that email-link is the only password-style sign-in method. Internally calls
// the same callable the public login form uses, so behavior stays consistent.
const handleAdminPasswordReset = async (emailToReset) => {
  if (!emailToReset) {
    showAppAlert("Cannot send sign-in link: user email is missing.");
    return;
  }
  if (emailToReset === user?.email) {
    showAppAlert("You can't send yourself a sign-in link from here. Sign out and use the regular sign-in form.");
    return;
  }

  if (!showAppConfirm(`Send a magic sign-in link to ${emailToReset}?`)) return;

  try {
    const result = await sendSignInLinkCallable({ email: emailToReset.toLowerCase().trim() });
    if (result?.data?.sent === false) {
      // Generic-success path: cloud function couldn't find a /users record.
      showAppAlert(`No user record found for ${emailToReset}. Use Invite a new LP to add them first.`);
    } else {
      showAppAlert(`Sign-in link sent to ${emailToReset}.`);
    }
  } catch (error) {
    console.error(`LPPortal: Error sending sign-in link to ${emailToReset}:`, error);
    showAppAlert(`Failed to send sign-in link: ${error.message}`);
  }
};
            const totalMembers = Array.isArray(sortedUsers) ? sortedUsers.length : 0;
            const totalAdmins = totalMembers > 0
              ? sortedUsers.filter(u => u.role === 'chapter_director' || u.role === 'superAdmin').length
              : 0;
            const totalLPs = totalMembers > 0
              ? sortedUsers.filter(u => u.role === 'lp').length
              : 0;
            return (
            <>
              <div className="win95-pane-header">
                <div>
                  <div className="win95-pane-header__context">
                    {isSuperAdmin ? "All chapters" : (userChapter || "All chapters")} · registered accounts
                  </div>
                  <h2 className="win95-pane-header__title">Users</h2>
                </div>
                <div className="win95-pane-header__stats">
                  <div className="win95-statpanel">
                    <div className="win95-statpanel__label">Members</div>
                    <div className="win95-statpanel__figure" style={{ color: "var(--mb-magenta-deep)" }}>
                      {totalMembers}
                    </div>
                  </div>
                  <div className="win95-statpanel">
                    <div className="win95-statpanel__label">LPs</div>
                    <div className="win95-statpanel__figure" style={{ color: "var(--mb-aqua-deep)" }}>
                      {totalLPs}
                    </div>
                  </div>
                  <div className="win95-statpanel">
                    <div className="win95-statpanel__label">Directors &amp; admins</div>
                    <div className="win95-statpanel__figure" style={{ color: "var(--mb-tangerine-deep)" }}>
                      {totalAdmins}
                    </div>
                  </div>
                </div>
              </div>

              <div className="admin-pane-body">
              {(!Array.isArray(sortedUsers) || sortedUsers.length === 0) ? (
                <EmptyState
                  icon={null}
                  title="No registered user accounts"
                  description="Users will appear here once they accept an invitation or sign in for the first time."
                />
              ) : (
                <div className="admin-list-scroll">
                  <div className="win95-listhead" style={{ gridTemplateColumns: USERS_GRID }}>
                    {["Member", "Role", "Chapter", "Profile", "Actions"].map((h) => (
                      <div key={h} className="win95-listhead__cell">{h}</div>
                    ))}
                  </div>
                  <div className="win95-listbody" role="list" aria-label="Registered user accounts">
                      {sortedUsers
                        // SuperAdmin sees everyone. Chapter directors / legacy admins are scoped to their own chapter.
                        .filter(u => isSuperAdmin || !userChapter || u.chapter === userChapter)
                        .map((u) => {
                          // Determine if controls should be disabled for this user row
                          const isSelf = u.uid === user.uid;
                          const targetIsSuperAdmin = u.role === 'superAdmin';
                          const disableRoleChange = isSelf || (targetIsSuperAdmin && !isSuperAdmin) || (!isAdmin);
                          // SuperAdmin can reassign anyone to any chapter.
                          // Chapter directors can change chapter on users in their chapter (enforced to own chapter in handleUpdateUser).
                          const disableChapterChange = isSelf || (!isSuperAdmin && !isChapterDirector);
                          const disablePasswordReset = isSelf;
                          const disableDelete = isSelf || (targetIsSuperAdmin && !isSuperAdmin);

                          const isEditing = !!editingUsers[u.uid];
                          const rowClass = isEditing ? 'admin-row-editing' : (isSelf ? 'admin-row-self' : '');
                          const editPanelInputStyle = { width: '100%', padding: '8px 10px', fontSize: '13px', fontFamily: 'var(--font-content)', boxSizing: 'border-box' };
                          // Same fallback the directory + edit-panel preview use.
                          const rowLegacyPhoto = u.name
                            ? `/assets/lps/${u.name.toLowerCase().replace(/\s+/g, '-').replace(/'/g, '')}.png`
                            : null;
                          const rowPhotoUrl = u.photoUrl || rowLegacyPhoto;
                          return (
                            <React.Fragment key={u.uid}>
                              <div
                                role="listitem"
                                className={`win95-row ${rowClass}`}
                                style={{ gridTemplateColumns: USERS_GRID }}
                              >
                                {/* Member: photo + name + email stacked */}
                                <div className="win95-row__cell win95-row__cell--primary">
                                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', minWidth: 0 }}>
                                    {rowPhotoUrl ? (
                                      <div className="member-row-avatar" style={{ position: 'relative', width: 32, height: 32, flexShrink: 0 }}>
                                        <MemberAvatarPlaceholder size={32} />
                                        <img
                                          src={rowPhotoUrl}
                                          alt=""
                                          className="member-photo"
                                          style={{
                                            position: 'absolute', inset: 0,
                                            width: 32, height: 32, objectFit: 'cover'
                                          }}
                                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                        />
                                      </div>
                                    ) : (
                                      <MemberAvatarPlaceholder size={32} className="member-row-avatar" />
                                    )}
                                    <div style={{ minWidth: 0, lineHeight: 1.2 }}>
                                      <div style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {u.name || <i style={{ color: 'var(--mb-ink-60)' }}>(No Name Set)</i>}
                                      </div>
                                      <div style={{ fontSize: 11, color: 'var(--mb-ink-60)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {u.email}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                {/* Role Select (+ chapterRole for superAdmins) */}
                                <div className="win95-row__cell win95-row__cell--controls">
                                  <div className="win95-select" style={{ width: '100%' }}>
                                    <select
                                      className="win95-select__native"
                                      aria-label={`Role for ${u.name || u.email}`}
                                      value={u.role || 'unauthorized'}
                                      onChange={(e) => handleUpdateUser(u.uid, "role", e.target.value)}
                                      disabled={disableRoleChange}
                                      style={{ flex: 1 }}
                                      title={disableRoleChange ? (isSelf ? "Cannot change own role" : "Permission denied") : "Change user role"}
                                    >
                                      <option value="unauthorized">Unauthorized</option>
                                      <option value="lp">LP</option>
                                      <option value="chapter_director">Chapter director</option>
                                      {(isSuperAdmin || targetIsSuperAdmin) && <option value="superAdmin">Super admin</option>}
                                    </select>
                                    <span className="win95-select__arrow" aria-hidden="true" />
                                  </div>
                                  {targetIsSuperAdmin && (
                                    <div className="win95-select" style={{ width: '100%', marginTop: 4 }}>
                                      <select
                                        className="win95-select__native"
                                        aria-label={`List ${u.name || u.email} on chapter page as`}
                                        value={u.chapterRole || ''}
                                        onChange={(e) => handleUpdateUser(u.uid, "chapterRole", e.target.value)}
                                        disabled={!isSuperAdmin}
                                        style={{ flex: 1, fontSize: 11 }}
                                        title="List this Super Admin on their chapter's public page"
                                      >
                                        <option value="">List as: — none —</option>
                                        <option value="lp">List as: LP</option>
                                        <option value="chapter_director">List as: chapter director</option>
                                      </select>
                                      <span className="win95-select__arrow" aria-hidden="true" />
                                    </div>
                                  )}
                                </div>
                                {/* Chapter Select */}
                                <div className="win95-row__cell win95-row__cell--controls">
                                  <div className="win95-select" style={{ width: '100%' }}>
                                    <select
                                      className="win95-select__native"
                                      aria-label={`Chapter for ${u.name || u.email}`}
                                      value={u.chapter || ""}
                                      onChange={(e) => handleUpdateUser(u.uid, "chapter", e.target.value)}
                                      disabled={disableChapterChange}
                                      style={{ flex: 1 }}
                                      title={disableChapterChange ? "Only SuperAdmin can change chapter (cannot change own)" : "Change user chapter"}
                                    >
                                      <option value="">— No chapter —</option>
                                      {activeChapterNames.map(c => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                    <span className="win95-select__arrow" aria-hidden="true" />
                                  </div>
                                </div>
                                {/* Profile: professional role (bold) + bio truncated */}
                                <div className="win95-row__cell win95-row__cell--profile" style={{ minWidth: 0 }}>
                                  <div style={{ lineHeight: 1.3, minWidth: 0 }}>
                                    <div style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                      {dashIfEmpty(u.professionalRole)}
                                    </div>
                                    <div style={{ fontSize: 11, color: 'var(--mb-ink-60)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                      {u.bio || <span style={{ fontStyle: 'italic' }}>No bio</span>}
                                    </div>
                                  </div>
                                </div>
                                {/* Actions: Edit / Close. Send link + Delete moved into the edit panel below.
                                    On mobile the label is swapped for a compact icon to free up
                                    horizontal room for the member name (see .user-action-btn CSS). */}
                                <div className="win95-row__cell win95-row__cell--actions">
                                  {isEditing ? (
                                    <RetroButton
                                      size="sm"
                                      className="user-action-btn"
                                      ariaLabel="Close editing"
                                      title="Close editing"
                                      onClick={() => {
                                        const newEditingUsers = { ...editingUsers };
                                        delete newEditingUsers[u.uid];
                                        setEditingUsers(newEditingUsers);
                                      }}
                                    >
                                      <span className="user-action-btn__label">Close</span>
                                      <svg className="user-action-btn__icon" width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square" aria-hidden="true">
                                        <path d="M4 4l8 8M12 4l-8 8" />
                                      </svg>
                                    </RetroButton>
                                  ) : (
                                    <RetroButton
                                      size="sm"
                                      className="user-action-btn"
                                      ariaLabel={`Edit profile for ${u.name || u.email}`}
                                      onClick={() => setEditingUsers({
                                        ...editingUsers,
                                        [u.uid]: {
                                          linkedinUrl: u.linkedinUrl || '',
                                          professionalRole: u.professionalRole || '',
                                          bio: u.bio || '',
                                          newEmail: u.email || ''
                                        }
                                      })}
                                      title={`Edit profile for ${u.email}`}
                                    >
                                      <span className="user-action-btn__label">Edit</span>
                                      <svg className="user-action-btn__icon" width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="square" strokeLinejoin="miter" aria-hidden="true">
                                        <path d="M10.5 2.5l3 3L6 13l-3.5.5L3 10z" />
                                      </svg>
                                    </RetroButton>
                                  )}
                                </div>
                              </div>
                              {isEditing && (
                                    <div className="retro-edit-panel">
                                      <div className="retro-edit-panel-title">
                                        Editing profile — {u.name || u.email}
                                      </div>
                                      {(() => {
                                        // Mirror the directory fallback so existing members
                                        // (whose photo lives at /assets/lps/{slug}.png and
                                        // hasn't been re-uploaded yet) preview correctly.
                                        const legacyPhoto = u.name
                                          ? `/assets/lps/${u.name.toLowerCase().replace(/\s+/g, '-').replace(/'/g, '')}.png`
                                          : null;
                                        const previewUrl = u.photoUrl || legacyPhoto;
                                        const isLegacy = !u.photoUrl && !!legacyPhoto;
                                        return (
                                      <div className="retro-form-row" style={{ marginBottom: 16 }}>
                                        <label>Member photo</label>
                                        <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                                          {previewUrl ? (
                                            <div style={{ position: 'relative', width: 72, height: 72, flexShrink: 0 }}>
                                              <MemberAvatarPlaceholder size={72} />
                                              <img
                                                src={previewUrl}
                                                alt={u.name || u.email}
                                                className="member-photo"
                                                style={{
                                                  position: 'absolute', inset: 0,
                                                  width: 72, height: 72, objectFit: 'cover'
                                                }}
                                                onError={(e) => {
                                                  // A broken legacy fallback (member with no matching
                                                  // /assets/lps/ file): hide the img so the silhouette
                                                  // placeholder underneath shows, and drop the "legacy"
                                                  // badge since there's no bundled photo after all.
                                                  e.currentTarget.style.display = 'none';
                                                  const badge = e.currentTarget.nextElementSibling;
                                                  if (badge) badge.style.display = 'none';
                                                }}
                                              />
                                              {isLegacy && (
                                                <div title="Bundled photo from /assets/lps/. Upload a new one to manage it from here." style={{
                                                  position: 'absolute', bottom: -2, right: -2,
                                                  background: 'var(--mb-ink)', color: 'var(--mb-chalk)',
                                                  fontSize: 9, padding: '1px 4px',
                                                  fontFamily: 'var(--font-content)'
                                                }}>
                                                  legacy
                                                </div>
                                              )}
                                            </div>
                                          ) : (
                                            <MemberAvatarPlaceholder size={72} />
                                          )}
                                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                            <input
                                              id={`memberPhoto-${u.uid}`}
                                              type="file"
                                              accept="image/*"
                                              disabled={!!uploadingPhotoFor[u.uid]}
                                              onChange={(e) => {
                                                const file = e.target.files && e.target.files[0];
                                                if (file) handleMemberPhotoUpload(u, file);
                                                e.target.value = '';
                                              }}
                                              style={{ fontFamily: 'var(--font-content)', fontSize: 12 }}
                                            />
                                            <div style={{ fontSize: 11, color: 'var(--mb-ink-60)' }}>
                                              {uploadingPhotoFor[u.uid]
                                                ? 'Uploading…'
                                                : 'PNG or JPG. Max 5 MB. Saves immediately.'}
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                        );
                                      })()}
                                      <div className="retro-form-grid">
                                        <div className="retro-form-row">
                                          <label htmlFor={`linkedin-${u.uid}`}>LinkedIn URL</label>
                                          <input
                                            id={`linkedin-${u.uid}`}
                                            type="url"
                                            value={editingUsers[u.uid].linkedinUrl || ''}
                                            onChange={(e) => setEditingUsers({
                                              ...editingUsers,
                                              [u.uid]: { ...editingUsers[u.uid], linkedinUrl: e.target.value }
                                            })}
                                            style={editPanelInputStyle}
                                            placeholder="https://linkedin.com/in/username"
                                          />
                                        </div>
                                        <div className="retro-form-row">
                                          <label htmlFor={`profrole-${u.uid}`}>Professional role</label>
                                          <input
                                            id={`profrole-${u.uid}`}
                                            type="text"
                                            value={editingUsers[u.uid].professionalRole || ''}
                                            onChange={(e) => setEditingUsers({
                                              ...editingUsers,
                                              [u.uid]: { ...editingUsers[u.uid], professionalRole: e.target.value }
                                            })}
                                            style={editPanelInputStyle}
                                            placeholder="e.g. CEO, Investor"
                                          />
                                        </div>
                                      </div>
                                      <div className="retro-form-row">
                                        <label htmlFor={`bio-${u.uid}`}>Bio</label>
                                        <textarea
                                          id={`bio-${u.uid}`}
                                          value={editingUsers[u.uid].bio || ''}
                                          onChange={(e) => setEditingUsers({
                                            ...editingUsers,
                                            [u.uid]: { ...editingUsers[u.uid], bio: e.target.value }
                                          })}
                                          style={{ ...editPanelInputStyle, minHeight: '110px', resize: 'vertical' }}
                                          placeholder="Short bio"
                                        />
                                      </div>
                                      {/* Account email lives apart from the profile fields above:
                                          it saves through the updateUserEmail callable (Auth +
                                          Firestore + notifications), not the Save Changes updateDoc. */}
                                      <div className="retro-form-row">
                                        <label htmlFor={`email-${u.uid}`}>Sign-in email</label>
                                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                          <input
                                            id={`email-${u.uid}`}
                                            type="email"
                                            value={editingUsers[u.uid].newEmail || ''}
                                            onChange={(e) => setEditingUsers({
                                              ...editingUsers,
                                              [u.uid]: { ...editingUsers[u.uid], newEmail: e.target.value }
                                            })}
                                            disabled={(targetIsSuperAdmin && !isSuperAdmin) || !!updatingEmailFor[u.uid]}
                                            style={{ ...editPanelInputStyle, flex: '1 1 220px', width: 'auto' }}
                                            placeholder="user@example.com"
                                          />
                                          <RetroButton
                                            size="sm"
                                            onClick={() => handleAdminChangeEmail(u, editingUsers[u.uid].newEmail)}
                                            disabled={
                                              (targetIsSuperAdmin && !isSuperAdmin) ||
                                              !!updatingEmailFor[u.uid] ||
                                              (editingUsers[u.uid].newEmail || '').trim().toLowerCase() === (u.email || '').toLowerCase()
                                            }
                                            title={
                                              targetIsSuperAdmin && !isSuperAdmin
                                                ? "Only Super Admins can change a Super Admin's email"
                                                : `Change the sign-in email for ${u.name || u.email}`
                                            }
                                          >
                                            {updatingEmailFor[u.uid] ? 'Updating…' : 'Update email'}
                                          </RetroButton>
                                        </div>
                                        <div style={{ fontSize: 11, color: 'var(--mb-ink-60)', marginTop: 4 }}>
                                          Changes where sign-in links go. The member keeps their badges and history, gets notified at both addresses, and any open session of theirs expires within about an hour.
                                        </div>
                                      </div>
                                      <div className="retro-action-row" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                                        <div style={{ display: 'flex', gap: 8 }}>
                                          <RetroButton
                                            variant="primary"
                                            onClick={async () => {
                                              const updates = editingUsers[u.uid];
                                              try {
                                                await updateDoc(doc(db, "users", u.uid), {
                                                  linkedinUrl: updates.linkedinUrl || '',
                                                  professionalRole: updates.professionalRole || '',
                                                  bio: updates.bio || ''
                                                });
                                                showAppAlert("User profile updated successfully.");
                                                const newEditingUsers = { ...editingUsers };
                                                delete newEditingUsers[u.uid];
                                                setEditingUsers(newEditingUsers);
                                                loadAdminData();
                                              } catch (error) {
                                                showAppAlert(`Error updating user: ${error.message}`);
                                              }
                                            }}
                                          >
                                            Save changes
                                          </RetroButton>
                                          <RetroButton
                                            onClick={() => {
                                              const newEditingUsers = { ...editingUsers };
                                              delete newEditingUsers[u.uid];
                                              setEditingUsers(newEditingUsers);
                                            }}
                                          >
                                            Cancel
                                          </RetroButton>
                                        </div>
                                        <div style={{ display: 'flex', gap: 8 }}>
                                          <RetroButton
                                            size="sm"
                                            onClick={() => handleAdminPasswordReset(u.email)}
                                            disabled={disablePasswordReset}
                                            title={disablePasswordReset ? "Can't send yourself a sign-in link from here" : `Email a sign-in link to ${u.email}`}
                                          >
                                            Send sign-in link
                                          </RetroButton>
                                          <RetroButton
                                            size="sm"
                                            variant="danger"
                                            onClick={() => handleDeleteUser(u.uid, u.email)}
                                            disabled={disableDelete}
                                            title={disableDelete ? (isSelf ? "Cannot delete self" : "Permission denied") : `Delete ${u.email}'s sign-in account and profile`}
                                          >
                                            Delete member
                                          </RetroButton>
                                        </div>
                                      </div>
                                    </div>
                              )}
                            </React.Fragment>
                          );
                        })}
                  </div>
                </div>
              )}
              </div>
            </>
            );
}
